/**
 * Plan phase (internally still 'schedule') — split-pane:
 *   • Left: timeline of sessions per day, grouped by date
 *   • Right: AI chat that drafts and edits the plan
 *
 * Real-time updates flow in through the existing BoardRoom socket — see
 * BoardView's onSocketMessage. We pull the latest plan-state once on mount
 * and after each chat round-trip.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { sanitiseChatText } from '../../shared/chat-sanitise';
import { UiNode } from '../lib/UiNode';
import { Avatar } from '../components/Avatar';
import {
  IconSparkle,
  IconSend,
  IconChat,
  IconCalendar,
  IconClock,
  IconUser,
  IconEye,
  IconEyeOff,
  IconTrash,
  IconPlus,
} from '../components/Icon';
import { SessionSubmissions } from '../components/SessionSubmissions';
import { CardDetail } from '../components/CardDetail';
import { SessionCompletion } from '../components/SessionCompletion';
import { BranchSelector } from '../components/BranchSelector';
import { LoadingBlock } from '../components/Spinner';
import type {
  BoardDetail,
  Cluster,
  PlanState,
  Session,
  PlanChatMessage,
  User,
} from '../../shared/types';

export function PlanPhase({
  board,
  clusters,
  planState,
  planLoading,
  user,
  activeBranchId,
  onActiveBranchIdChange,
  onPlanStateChange,
}: {
  board: BoardDetail;
  clusters: Cluster[];
  planState: PlanState;
  planLoading: boolean;
  user: User;
  activeBranchId: string | null;
  onActiveBranchIdChange: (next: string | null) => void;
  onPlanStateChange: (next: PlanState) => void;
}) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setActiveBranchId = onActiveBranchIdChange;
  // Switching branches mid-session triggers its own fetch. Suppress the
  // empty state during that refetch so users don't see a flash of "No plan yet".
  const [branchSwitching, setBranchSwitching] = useState(false);
  const initialMountRef = useRef(true);
  const isOwner = board.role === 'owner';

  // When the active branch changes, refetch plan state for that branch.
  useEffect(() => {
    if (initialMountRef.current) {
      initialMountRef.current = false;
      return;
    }
    let cancelled = false;
    setBranchSwitching(true);
    void api
      .planState(board.id, activeBranchId)
      .then((s) => {
        if (!cancelled) onPlanStateChange(s);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setBranchSwitching(false);
      });
    return () => {
      cancelled = true;
    };
    // onPlanStateChange purposely omitted — it's a setter that doesn't need to retrigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board.id, activeBranchId]);

  const stillLoading = planLoading || branchSwitching;
  const noPlanYet = !stillLoading && planState.sessions.length === 0;

  async function generate() {
    if (!isOwner || generating) return;
    setError(null);
    setGenerating(true);
    try {
      const r = await api.generatePlan(board.id, activeBranchId);
      onPlanStateChange(r.state);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to generate');
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <BranchSelector
          board={board}
          activeBranchId={activeBranchId}
          onChange={setActiveBranchId}
          onMerged={() => {
            // After a merge, refetch main.
            void api.planState(board.id, null).then(onPlanStateChange).catch(() => undefined);
          }}
        />
        {activeBranchId && (
          <span className="text-xs text-cream-3">
            Viewing a scenario — your edits and AI changes stay here until you make it the plan.
          </span>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_minmax(320px,400px)] lg:grid-rows-[1fr]">
        <div className="min-w-0">
          {stillLoading ? (
            <PlanLoadingState />
          ) : noPlanYet ? (
            <EmptyState
              isOwner={isOwner}
              onGenerate={generate}
              generating={generating}
              board={board}
            />
          ) : (
            <PlanTimeline
              board={board}
              clusters={clusters}
              planState={planState}
              user={user}
              isOwner={isOwner}
              activeBranchId={activeBranchId}
              onPlanStateChange={onPlanStateChange}
              onRegenerate={generate}
              regenerating={generating}
            />
          )}
          {error && (
            <p className="mt-3 text-sm text-rust">{error}</p>
          )}
        </div>

        <PlanChat
          board={board}
          planState={planState}
          activeBranchId={activeBranchId}
          onPlanStateChange={onPlanStateChange}
          disabled={noPlanYet}
        />
      </div>
    </div>
  );
}

function PlanLoadingState() {
  return (
    <section className="panel p-4">
      <LoadingBlock label="Fetching the latest plan…" hint="Hold tight." />
    </section>
  );
}

// ============================================================================
// Session templates
// ============================================================================

interface SessionTemplate {
  id: string;
  name: string;
  emoji: string;
  duration: number; // minutes
  colorHue: number;
}

const SESSION_TEMPLATES: SessionTemplate[] = [
  { id: 'standup', name: 'Standup', emoji: '🪧', duration: 15, colorHue: 200 },
  { id: 'demo', name: 'Demo', emoji: '🎬', duration: 5, colorHue: 320 },
  { id: 'session', name: 'Session', emoji: '💬', duration: 45, colorHue: 28 },
  { id: 'workshop', name: 'Workshop', emoji: '🛠️', duration: 60, colorHue: 90 },
  { id: 'keynote', name: 'Keynote', emoji: '🎤', duration: 90, colorHue: 260 },
  { id: 'break', name: 'Break', emoji: '☕', duration: 15, colorHue: 40 },
];

function SessionTemplateMenu({ onPick }: { onPick: (tpl: SessionTemplate) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <UiNode
        as="button"
        name="plan-add-session-button"
        entity="session"
        action="create_start"
        className="btn btn-ghost inline-flex items-center gap-1.5"
        onClick={() => setOpen((v) => !v)}
      >
        <IconPlus size={14} />
        <span className="hidden sm:inline">Add session</span>
      </UiNode>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-20 mt-1 flex w-56 flex-col gap-0.5 rounded-lg border border-ink-rim/80 bg-ink-2 p-1 shadow-xl">
            {SESSION_TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-ink-3"
                onClick={() => {
                  setOpen(false);
                  onPick(t);
                }}
              >
                <span aria-hidden>{t.emoji}</span>
                <span className="flex-1">{t.name}</span>
                <span className="font-mono text-xs text-cream-3">{t.duration}m</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Empty state — nudge the owner to generate
// ============================================================================

function EmptyState({
  isOwner,
  onGenerate,
  generating,
  board,
}: {
  isOwner: boolean;
  onGenerate: () => void;
  generating: boolean;
  board: BoardDetail;
}) {
  const ready = !!board.startsOn && !!board.endsOn;
  return (
    <div className="panel flex flex-col items-center gap-3 p-8 text-center">
      <IconSparkle size={28} />
      <h2 className="text-base">Draft your plan</h2>
      <p className="max-w-md text-sm text-cream-3">
        Let the AI lay out an initial schedule based on your themes, dates and team
        availability. You can then chat with it on the right to refine.
      </p>
      {!ready && (
        <p className="text-xs text-rust">
          Add a start and end date in Setup first — the AI needs to know the date range.
        </p>
      )}
      {isOwner && ready && (
        <UiNode
          as="button"
          name="plan-generate-button"
          entity="plan"
          action="generate"
          state={generating ? 'busy' : 'idle'}
          className="btn btn-primary inline-flex items-center gap-1.5"
          onClick={onGenerate}
          disabled={generating}
        >
          <IconSparkle size={14} />
          {generating ? 'Drafting…' : 'Generate plan'}
        </UiNode>
      )}
      {!isOwner && (
        <p className="text-xs text-cream-3">Ask the board owner to draft a plan.</p>
      )}
    </div>
  );
}

// ============================================================================
// Timeline — days with sessions
// ============================================================================

function PlanTimeline({
  board,
  clusters,
  planState,
  user,
  isOwner,
  activeBranchId,
  onPlanStateChange,
  onRegenerate,
  regenerating,
}: {
  board: BoardDetail;
  clusters: Cluster[];
  planState: PlanState;
  user: User;
  isOwner: boolean;
  activeBranchId: string | null;
  onPlanStateChange: (next: PlanState) => void;
  onRegenerate: () => void;
  regenerating: boolean;
}) {
  const byDate = useMemo(() => {
    const m = new Map<string, Session[]>();
    for (const s of planState.sessions) {
      const arr = m.get(s.date) ?? [];
      arr.push(s);
      m.set(s.date, arr);
    }
    for (const [, arr] of m) arr.sort((a, b) => a.startMinute - b.startMinute);
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [planState.sessions]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base">The plan</h2>
        <div className="flex items-center gap-1.5">
          {isOwner && (
            <SessionTemplateMenu
              onPick={async (tpl) => {
                const first = byDate[0]?.[0] ?? board.startsOn ?? new Date().toISOString().slice(0, 10);
                // Anchor at end of last session on that day, else 09:00
                const onThatDay = planState.sessions
                  .filter((s) => s.date === first)
                  .sort((a, b) => b.endMinute - a.endMinute);
                const start = onThatDay[0]?.endMinute ?? board.workingStartMinute ?? 540;
                try {
                  const r = await api.createSession(board.id, {
                    name: tpl.name,
                    date: first,
                    startMinute: start,
                    endMinute: start + tpl.duration,
                    colorHue: tpl.colorHue,
                    branchId: activeBranchId,
                  });
                  onPlanStateChange({
                    ...planState,
                    sessions: [...planState.sessions, r.session],
                  });
                } catch (e) {
                  console.error(e);
                }
              }}
            />
          )}
          {isOwner && (
            <UiNode
              as="button"
              name="plan-regenerate-button"
              entity="plan"
              action="generate"
              state={regenerating ? 'busy' : 'idle'}
              className="btn btn-ghost inline-flex items-center gap-1.5"
              onClick={onRegenerate}
              disabled={regenerating}
              title="Replace the current plan with a fresh AI draft"
            >
              <IconSparkle size={14} />
              <span className="hidden sm:inline">{regenerating ? 'Drafting…' : 'Regenerate'}</span>
            </UiNode>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {byDate.map(([date, sessions]) => (
          <DayCard
            key={date}
            date={date}
            sessions={sessions}
            clusters={clusters}
            board={board}
            user={user}
            isOwner={isOwner}
            planState={planState}
            onPlanStateChange={onPlanStateChange}
          />
        ))}
      </div>
    </div>
  );
}

function DayCard({
  date,
  sessions,
  clusters,
  board,
  user,
  isOwner,
  planState,
  onPlanStateChange,
}: {
  date: string;
  sessions: Session[];
  clusters: Cluster[];
  board: BoardDetail;
  user: User;
  isOwner: boolean;
  planState: PlanState;
  onPlanStateChange: (next: PlanState) => void;
}) {
  const dayLabel = useMemo(() => formatDateLabel(date), [date]);
  return (
    <section className="panel p-4">
      <header className="mb-3 flex items-center gap-2">
        <IconCalendar size={14} />
        <h3 className="text-sm font-medium">{dayLabel}</h3>
        <span className="text-xs text-cream-3">{sessions.length} session{sessions.length === 1 ? '' : 's'}</span>
      </header>
      <ul className="flex flex-col gap-2">
        {sessions.map((s) => (
          <SessionRow
            key={s.id}
            session={s}
            board={board}
            user={user}
            clusters={clusters}
            isOwner={isOwner}
            planState={planState}
            onPlanStateChange={onPlanStateChange}
          />
        ))}
      </ul>
    </section>
  );
}

function SessionRow({
  session,
  board,
  user,
  clusters,
  isOwner,
  planState,
  onPlanStateChange,
}: {
  session: Session;
  board: BoardDetail;
  user: User;
  clusters: Cluster[];
  isOwner: boolean;
  planState: PlanState;
  onPlanStateChange: (next: PlanState) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const owner = board.members.find((m) => m.id === session.ownerId) ?? null;
  const themes = session.themeIds
    .map((id) => clusters.find((c) => c.id === id))
    .filter((c): c is Cluster => !!c);

  function update<K extends keyof Session>(field: K, value: Session[K]): void {
    if (!isOwner) return;
    const next = { ...session, [field]: value };
    onPlanStateChange({
      ...planState,
      sessions: planState.sessions.map((s) => (s.id === session.id ? next : s)),
    });
    void api.patchSession(board.id, session.id, { [field]: value } as Parameters<typeof api.patchSession>[2]);
  }

  async function remove() {
    if (!isOwner) return;
    if (!confirm(`Delete "${session.name}"?`)) return;
    onPlanStateChange({
      ...planState,
      sessions: planState.sessions.filter((s) => s.id !== session.id),
    });
    try {
      await api.deleteSession(board.id, session.id);
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <li
      className="flex flex-col gap-2 rounded-lg border border-ink-rim/60 bg-ink-2 p-3"
      style={{ borderLeft: `3px solid hsl(${session.colorHue}, 70%, 55%)` }}
    >
      <header className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          {isOwner ? (
            <input
              className="w-full bg-transparent text-sm font-medium focus:outline-none"
              value={session.name}
              onChange={(e) => update('name', e.target.value)}
            />
          ) : (
            <h4 className="truncate text-sm font-medium">{session.name}</h4>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-cream-3">
            <span className="inline-flex items-center gap-1">
              <IconClock size={12} />
              {minutesToTime(session.startMinute)} – {minutesToTime(session.endMinute)}
            </span>
            {owner && (
              <span className="inline-flex items-center gap-1">
                <Avatar user={owner} size={16} />
                {owner.name.split(' ')[0]}
              </span>
            )}
            {!owner && isOwner && (
              <span className="inline-flex items-center gap-1 text-cream-4">
                <IconUser size={12} />
                Unowned
              </span>
            )}
            {!session.isRevealed && (
              <span className="chip inline-flex items-center gap-1">
                <IconEyeOff size={12} />
                Hidden
              </span>
            )}
            <SessionCompletion board={board} sessionId={session.id} me={user} />
          </div>
          {themes.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {themes.map((t) => (
                <span
                  key={t.id}
                  className="chip"
                  style={{
                    background: `hsla(${t.colorHue}, 70%, 55%, 0.18)`,
                    borderColor: `hsla(${t.colorHue}, 70%, 55%, 0.4)`,
                  }}
                >
                  {t.title}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          {isOwner && (
            <button
              type="button"
              className="btn btn-ghost p-1.5"
              onClick={() => update('isRevealed', !session.isRevealed)}
              title={session.isRevealed ? 'Hide from team until revealed' : 'Reveal to team'}
            >
              {session.isRevealed ? <IconEye size={14} /> : <IconEyeOff size={14} />}
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost p-1.5"
            onClick={() => setDetailOpen(true)}
            title="Open discussion"
          >
            <IconChat size={14} />
          </button>
          <button
            type="button"
            className="btn btn-ghost p-1.5"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? '−' : '+'}
          </button>
          {isOwner && (
            <button
              type="button"
              className="btn btn-ghost p-1.5 text-rust"
              onClick={remove}
              title="Delete session"
            >
              <IconTrash size={14} />
            </button>
          )}
        </div>
      </header>

      <CardDetail
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        board={board}
        kind="session"
        subjectId={session.id}
        header={
          <span className="inline-flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: `hsl(${session.colorHue}, 70%, 55%)` }}
            />
            <span className="truncate">{session.name}</span>
          </span>
        }
        body={
          session.description ? (
            <p className="rounded-md bg-ink-3 p-2 text-cream-2">{session.description}</p>
          ) : null
        }
      />

      {expanded && (
        <>
          <SessionEditor
            session={session}
            board={board}
            clusters={clusters}
            isOwner={isOwner}
            onUpdate={update}
          />
          <SessionSubmissions board={board} sessionId={session.id} />
        </>
      )}
    </li>
  );
}

function SessionEditor({
  session,
  board,
  clusters,
  isOwner,
  onUpdate,
}: {
  session: Session;
  board: BoardDetail;
  clusters: Cluster[];
  isOwner: boolean;
  onUpdate: <K extends keyof Session>(f: K, v: Session[K]) => void;
}) {
  return (
    <div className="flex flex-col gap-2 border-t border-ink-rim/60 pt-2">
      <textarea
        className="input min-h-[60px] text-sm"
        placeholder="What happens in this session?"
        defaultValue={session.description ?? ''}
        disabled={!isOwner}
        onBlur={(e) => {
          const v = e.currentTarget.value;
          if ((v || null) !== session.description) onUpdate('description', v || null);
        }}
      />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <label className="flex flex-col gap-1 text-xs text-cream-3">
          Date
          <input
            type="date"
            className="input"
            defaultValue={session.date}
            disabled={!isOwner}
            onBlur={(e) => {
              if (e.currentTarget.value && e.currentTarget.value !== session.date)
                onUpdate('date', e.currentTarget.value);
            }}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-cream-3">
          Start
          <input
            type="time"
            className="input"
            defaultValue={minutesToTime(session.startMinute)}
            disabled={!isOwner}
            onBlur={(e) => {
              const m = timeToMinutes(e.currentTarget.value);
              if (m !== null && m !== session.startMinute) onUpdate('startMinute', m);
            }}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-cream-3">
          End
          <input
            type="time"
            className="input"
            defaultValue={minutesToTime(session.endMinute)}
            disabled={!isOwner}
            onBlur={(e) => {
              const m = timeToMinutes(e.currentTarget.value);
              if (m !== null && m !== session.endMinute) onUpdate('endMinute', m);
            }}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-cream-3">
          Owner
          <select
            className="input"
            value={session.ownerId ?? ''}
            disabled={!isOwner}
            onChange={(e) => onUpdate('ownerId', e.target.value || null)}
          >
            <option value="">— unowned —</option>
            {board.members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {clusters.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-cream-3">Themes</span>
          <div className="flex flex-wrap gap-1.5">
            {clusters.map((c) => {
              const on = session.themeIds.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  disabled={!isOwner}
                  className="chip"
                  style={{
                    background: on ? `hsla(${c.colorHue}, 70%, 55%, 0.28)` : undefined,
                    borderColor: on ? `hsla(${c.colorHue}, 70%, 55%, 0.6)` : undefined,
                    opacity: on ? 1 : 0.55,
                  }}
                  onClick={() => {
                    const next = on
                      ? session.themeIds.filter((id) => id !== c.id)
                      : [...session.themeIds, c.id];
                    onUpdate('themeIds', next);
                  }}
                >
                  {c.title}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <AttendeesRow session={session} board={board} isOwner={isOwner} onUpdate={onUpdate} />
    </div>
  );
}

/**
 * Additional attendees row. The session's `ownerId` is implicitly a
 * required attendee — they don't appear in this list. Each board
 * member here can be off, in as Optional (visible but doesn't block
 * cross-board scheduling), or in as Required (blocks cross-board
 * scheduling). Three-state click cycle: off → required → optional → off.
 */
function AttendeesRow({
  session,
  board,
  isOwner,
  onUpdate,
}: {
  session: Session;
  board: BoardDetail;
  isOwner: boolean;
  onUpdate: <K extends keyof Session>(f: K, v: Session[K]) => void;
}) {
  const candidates = board.members.filter((m) => m.id !== session.ownerId);
  if (candidates.length === 0) return null;

  function stateFor(userId: string): 'off' | 'required' | 'optional' {
    const a = session.attendees.find((x) => x.userId === userId);
    if (!a) return 'off';
    return a.required ? 'required' : 'optional';
  }

  function cycle(userId: string) {
    if (!isOwner) return;
    const cur = stateFor(userId);
    let next: typeof session.attendees;
    if (cur === 'off') {
      next = [...session.attendees, { userId, required: true }];
    } else if (cur === 'required') {
      next = session.attendees.map((a) =>
        a.userId === userId ? { ...a, required: false } : a,
      );
    } else {
      next = session.attendees.filter((a) => a.userId !== userId);
    }
    onUpdate('attendees', next);
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-cream-3">
        Also attending{' '}
        <span className="text-[10px] text-cream-3/70">
          (tap to add: required → optional → off)
        </span>
      </span>
      <div className="flex flex-wrap gap-1.5">
        {candidates.map((m) => {
          const s = stateFor(m.id);
          const on = s !== 'off';
          return (
            <button
              key={m.id}
              type="button"
              disabled={!isOwner}
              className="chip inline-flex items-center gap-1"
              style={{
                background: on ? 'hsla(28, 60%, 55%, 0.22)' : undefined,
                borderColor: on ? 'hsla(28, 60%, 55%, 0.6)' : undefined,
                opacity: on ? 1 : 0.55,
              }}
              onClick={() => cycle(m.id)}
              title={
                s === 'required'
                  ? 'Required — blocks them on other boards'
                  : s === 'optional'
                    ? "Optional — doesn't block other boards"
                    : 'Not attending'
              }
            >
              <span>{m.name}</span>
              {s === 'required' && <span className="text-[9px] opacity-80">required</span>}
              {s === 'optional' && <span className="text-[9px] opacity-80">optional</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Chat panel
// ============================================================================

function PlanChat({
  board,
  planState,
  activeBranchId,
  onPlanStateChange,
  disabled,
}: {
  board: BoardDetail;
  planState: PlanState;
  activeBranchId: string | null;
  onPlanStateChange: (next: PlanState) => void;
  disabled: boolean;
}) {
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [planState.chat]);

  async function send() {
    const msg = input.trim();
    if (!msg || busy) return;
    setBusy(true);
    setError(null);
    setInput('');
    try {
      const r = await api.chatPlan(board.id, msg, activeBranchId);
      onPlanStateChange(r.state);
    } catch (e: unknown) {
      const message =
        e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed';
      setError(message);
      setInput(msg); // restore the unsent message
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside className="panel flex min-h-[480px] flex-col gap-2 p-3 lg:sticky lg:top-4 lg:max-h-[calc(100vh-7rem)]">
      <header className="flex items-center gap-2 px-1">
        <IconSparkle size={14} />
        <h2 className="text-sm font-medium">Chat with the plan</h2>
        <span className="ml-auto text-[10px] uppercase tracking-wide text-cream-4">AI</span>
      </header>

      <div ref={scrollerRef} className="flex flex-1 flex-col gap-2 overflow-y-auto px-1">
        {planState.chat.length === 0 && (
          <p className="my-auto px-2 text-center text-xs text-cream-3">
            {disabled
              ? 'Generate a plan first, then chat here to refine it.'
              : 'Ask me to move things around, add breaks, split a session, swap owners.'}
          </p>
        )}
        {planState.chat.map((m) => (
          <ChatBubble key={m.id} message={m} board={board} />
        ))}
        {busy && (
          <ChatBubble
            board={board}
            message={{
              id: '__pending',
              role: 'assistant',
              content: 'Thinking…',
              authorId: null,
              authorName: null,
              createdAt: Math.floor(Date.now() / 1000),
            }}
          />
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
        className="flex items-end gap-2 border-t border-ink-rim/60 pt-2"
      >
        <textarea
          className="input min-h-[64px] max-h-[240px] flex-1 resize-y text-sm leading-relaxed"
          rows={3}
          value={input}
          disabled={disabled || busy}
          placeholder={
            disabled
              ? 'Generate a plan first…'
              : 'Move kickoff to 10am, give clay the demo slot…\n(Shift+Enter for newline, ⌘/Ctrl+Enter to send)'
          }
          onChange={(e) => {
            setInput(e.target.value);
            // Auto-grow: the textarea expands as you type until the
            // max-h kicks in and the inner scroll takes over. Cheaper
            // than a measurement library and behaves on every browser.
            const el = e.currentTarget;
            el.style.height = 'auto';
            el.style.height = `${Math.min(240, el.scrollHeight)}px`;
          }}
          onKeyDown={(e) => {
            // ⌘/Ctrl+Enter always sends (matches Slack/Linear/Notion).
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void send();
              return;
            }
            // Plain Enter sends; Shift+Enter inserts a newline. The
            // chat convention every messaging app uses.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <UiNode
          as="button"
          name="plan-chat-send"
          entity="chat"
          action="send"
          state={busy ? 'busy' : 'idle'}
          type="submit"
          className="btn btn-primary inline-flex items-center gap-1"
          disabled={disabled || busy || !input.trim()}
        >
          <IconSend size={14} />
          <span className="hidden sm:inline">Send</span>
        </UiNode>
      </form>
      {error && <p className="px-1 text-xs text-rust">{error}</p>}
    </aside>
  );
}

function ChatBubble({
  message,
  board,
}: {
  message: PlanChatMessage;
  board: BoardDetail;
}) {
  const isAssistant = message.role === 'assistant';
  const author = !isAssistant
    ? board.members.find((m) => m.id === message.authorId) ?? null
    : null;
  return (
    <div
      className={`flex gap-2 ${isAssistant ? 'flex-row' : 'flex-row-reverse'}`}
    >
      <div
        className={`mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs ${
          isAssistant ? 'bg-clove text-ink' : 'bg-ink-3'
        }`}
        title={isAssistant ? 'AI' : message.authorName ?? 'You'}
      >
        {isAssistant ? <IconSparkle size={12} /> : author ? <Avatar user={author} size={20} /> : '·'}
      </div>
      <div
        className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm ${
          isAssistant
            ? 'rounded-tl-sm bg-ink-2 text-cream-1'
            : 'rounded-tr-sm bg-clove/30 text-cream-1'
        }`}
      >
        {/* Defence-in-depth: even though ai-plan.ts already
            sanitises, historical rows from before the server guard
            shipped, or any future model-prompt-injection, could
            still leak JSON. Scrub on render too. User messages
            stay verbatim — only the assistant side is suspect. */}
        {isAssistant ? sanitiseChatText(message.content) : message.content}
      </div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function minutesToTime(m: number): string {
  const h = Math.floor(m / 60)
    .toString()
    .padStart(2, '0');
  const mm = (m % 60).toString().padStart(2, '0');
  return `${h}:${mm}`;
}

function timeToMinutes(s: string): number | null {
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

function formatDateLabel(iso: string): string {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
